import { CheckCircleOutlined, CloudSyncOutlined, CloudUploadOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Popconfirm, Space, Spin, Table, Tag, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { getSyncConfig, syncAll } from '../../services/syncService';
import { deleteUser, getAllUsers } from '../../services/userService';

const UserList = ({ refreshTrigger, onEditUser }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncingAll, setSyncingAll] = useState(false);
    const isMobile = window.innerWidth < 768;
    const [config, setConfig] = useState({ interval: 30, intervalUnit: 'segundos' });

    // Cargar la configuración al inicio
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const syncConfig = await getSyncConfig();
                setConfig(syncConfig);
            } catch (error) {
                console.error('Error al cargar configuración:', error);
            }
        };

        fetchConfig();
    }, []);

    // Cargar usuarios al inicio y cuando cambie el trigger
    useEffect(() => {
        loadUsers();
    }, [refreshTrigger]);

    // Actualizar la lista según el intervalo configurado
    useEffect(() => {
        console.log({ config });
        const interval = setInterval(() => {
            loadUsers();
        }, config.interval * (config.intervalUnit === 'segundos' ? 1000 : config.intervalUnit === 'minutos' ? 60000 : 3600000)); // Convertir a milisegundos

        // Limpiar intervalo al desmontar
        return () => clearInterval(interval);
    }, [config]);

    const loadUsers = async () => {
        try {
            console.log('Cargando usuarios...');
            setLoading(true);
            const userList = await getAllUsers();
            setUsers(userList);
        } catch (error) {
            window.messageApi.error('Error al cargar usuarios: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteUser(id);
            // Actualizar la lista de usuarios
            setUsers(users.filter(user => user.id !== id));
            window.messageApi.success('Usuario eliminado correctamente');
        } catch (error) {
            window.messageApi.error('Error al eliminar usuario: ' + error.message);
        }
    };

    const handleEdit = (user) => {
        if (onEditUser) {
            onEditUser(user);
        }
    };

    // Renderizar el estado de sincronización
    const renderSyncStatus = (pendingSync, lastSynced) => {
        if (pendingSync === 1) {
            return (
                <Tooltip title="Pendiente de sincronizar">
                    <Tag icon={<CloudUploadOutlined />} color="warning">
                        Pendiente
                    </Tag>
                </Tooltip>
            );
        } else {
            const syncDate = lastSynced
                ? new Date(lastSynced).toLocaleString()
                : 'Nunca';

            return (
                <Tooltip title={`Última sincronización: ${syncDate}`}>
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        Sincronizado
                    </Tag>
                </Tooltip>
            );
        }
    };

    // Sincronizar todos los usuarios
    const handleSyncAll = async () => {
        try {
            setSyncingAll(true);
            await syncAll();
            // Recargar la lista para reflejar los cambios
            await loadUsers();
            window.messageApi.success('Sincronización completada');
        } catch (error) {
            window.messageApi.error('Error durante la sincronización: ' + error.message);
        } finally {
            setSyncingAll(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'No.',
            dataIndex: 'No',
            key: 'No',
            render: No => No || 'Pendiente',
        },
        {
            title: 'Nombre',
            dataIndex: 'firstName',
            key: 'firstName',
        },
        {
            title: 'Apellidos',
            dataIndex: 'lastName',
            key: 'lastName',
        },
        {
            title: 'Usuario',
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: 'Correo',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Admin',
            dataIndex: 'Admin',
            key: 'Admin',
            render: (isAdmin) => (
                <Tag color={isAdmin ? 'green' : 'default'}>
                    {isAdmin ? 'Sí' : 'No'}
                </Tag>
            ),
        },
        {
            title: 'Estado',
            key: 'syncStatus',
            render: (_, record) => renderSyncStatus(record.pendingSync, record.lastSynced),
        },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEdit(record)}
                    >
                        Editar
                    </Button>
                    <Popconfirm
                        title="¿Está seguro de que desea eliminar este usuario?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        >
                            Eliminar
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    if (loading) {
        return <Spin size="large" />;
    }

    if (users.length === 0) {
        return <Empty description="No hay usuarios registrados aún" />;
    }

    return (
        <div className="user-list-container">
            <Card
                title={isMobile ? 'Usuarios' : 'Usuarios Registrados'}
                extra={
                    <Button
                        icon={<CloudSyncOutlined />}
                        onClick={handleSyncAll}
                        loading={syncingAll}
                        type="primary"
                    >
                        {isMobile ? '' : 'Sincronizar Todos'}
                    </Button>
                }
            >
                <Table
                    columns={columns}
                    dataSource={users.map(user => ({ ...user, key: user.id }))}
                    pagination={{ pageSize: 5 }}
                    bordered
                    size="middle"
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
};

export default UserList; 